/**
 * Build kitchen backsplash wall regions from a styled room. Pure.
 *
 * Produces two kinds of `surfaceType: 'backsplash'` regions:
 *   - counter: one band per STRETCH OF COUNTER on a wall — the counter's own
 *     pieces merged, so the band stops wherever the counter does — spanning
 *     the countertop top up to the underside of the upper cabinet hanging
 *     over each part of it (stepping up under a shorter, bridge cabinet), or
 *     up behind a valance bay to its top; where nothing hangs over it, the
 *     lowest underside on that wall; where the wall has no uppers it falls
 *     back to an 18" band (or 3" when `config.counterBacksplash.lowProfile`).
 *   - stove: a band behind each range, up to the bottom of what hangs
 *     directly above the stove's span on the same wall (over-range cabinet,
 *     a Venus wood hood's insert, or a metal hood when
 *     `config.stoveBacksplash.toHood`), else the same top the counter band
 *     uses on that wall. Never the room ceiling — in a sloped-ceiling room
 *     the tallest wall tops out far above the run wall.
 *
 * Both bands are carved around the wall's windows and door-less cased
 * openings (`room.windows` / `room.openings`, hydrated copies with geometry)
 * whenever the opening's vertical extent overlaps the band's Y range — only
 * the GLASS is off limits: the band still fills the wall below the sill and
 * above the head (sill/head strips) when those slices clear a minimum height.
 *
 * Wall association is via each kitchen object's `parentWallIds[0]` (cabinets,
 * countertops, ranges all carry it), so no Three.js / mesh lookup is needed.
 * Geometry comes from the shared ./backsplashRegion helpers.
 *
 * @returns {Array<{wallId:string, region:{x,y,width,height}, surfaceType:'backsplash', type:'texture', textureId:?string, styleProvenance:object, kind:'counter'|'stove'}>}
 */

const {
  projectExtentOntoWall,
  objectAbutsWall,
  bandRegion,
} = require('./backsplashRegion');
const {
  isUnchosenBacksplash,
  clearUnchosenBacksplash,
} = require('./unchosenBacksplash');
const { wallFaceHeight } = require('../../geometry/wallFace');
const { woodHoodInsertRiseM } = require('../../cabinet-runs/woodHoods');
const { isWoodHood } = require('../../fixture-types/hoodForm');
const { KITCHEN_CABINET_DIMENSIONS } = require('../constants');
const {
  readBacksplashSpec,
  wallSetsBand,
  wallTopY,
  sillOverCounter,
  counterLineForSpec,
  capStepsAtLine,
  rangeTopForSpec,
} = require('./backsplashSpec');

const INCHES_TO_M = 0.0254;
const DEFAULT_COUNTER_BACKSPLASH_M = 18 * INCHES_TO_M;
const LOW_PROFILE_M = 3 * INCHES_TO_M;

function typeOf(o) {
  return (o.type || o.objectType || '').toLowerCase();
}
function cat(o, k) {
  return Boolean(o.category && typeof o.category === 'object' && o.category[k]);
}
function isCountertop(o) {
  return typeOf(o) === 'countertop' || cat(o, 'countertop');
}
function isLowerCab(o) {
  return typeOf(o) === 'lower-cabinet' || cat(o, 'lower-cabinet');
}
/**
 * A cabinet that stands THROUGH the band rather than under it: taller than a
 * base cabinet by more than a backsplash. A pantry, a fridge surround and a
 * tall filler are all typed `lower-cabinet`, and none of them is a counter —
 * taken as one, a tall filler alone against a side wall set that wall's
 * counter line at its own 94" top and put a strip of tile at the ceiling.
 */
function standsThroughBand(o) {
  const h = o.dimensions?.y ?? o.dimensions?.height ?? 0;
  return h > KITCHEN_CABINET_DIMENSIONS.height + DEFAULT_COUNTER_BACKSPLASH_M;
}
/**
 * An upper whose underside sets the band's top. A STACKED upper stands on
 * another upper (`category.stack`, plan 2026-10-21, C24) — its underside is a
 * cabinet top, never the band's, so it is not one.
 */
function isUpperCab(o) {
  if (cat(o, 'stack')) return false;
  return typeOf(o) === 'upper-cabinet' || cat(o, 'upper-cabinet');
}
/**
 * A valance bay (`category.valanceBay`): a valance hung in a gap in the
 * uppers, over a sink. Its underside is no cabinet's — it is an arched board
 * — so the band runs up BEHIND it to its top: the valance hides the tile's
 * edge and the wall under its arch is tiled (the user, 2026-11-01).
 */
function isValanceBay(o) {
  return cat(o, 'valanceBay');
}
/** How high the counter band runs under an upper: its underside, or behind a valance bay to its top. */
function bandTopUnder(o) {
  return isValanceBay(o) ? objTopY(o) : objBottomY(o);
}
function isStove(o) {
  return (
    ['range', 'stove'].includes(typeOf(o)) || cat(o, 'range') || cat(o, 'stove')
  );
}
function isHood(o) {
  return (
    typeOf(o) === 'vent-hood' ||
    typeOf(o) === 'range-hood' ||
    cat(o, 'vent-hood')
  );
}
/**
 * Where the band stops under a hood: its underside — or, under a Venus wood
 * hood, its insert (`woodHoodInsertRiseM`). The wood front drops below the
 * insert to hide it but the back does not, so the wall shows up to the
 * insert (the user, 2026-11-01: "the wood box doesnt come all the way down on
 * the backside").
 */
function hoodBandTopY(hood) {
  const rise = woodHoodInsertRiseM(hood);
  return rise === null ? objBottomY(hood) : objBottomY(hood) + rise;
}
/** A microwave — over a range, it stops a band set to run past the hood. */
function isMicrowave(o) {
  return typeOf(o) === 'microwave' || cat(o, 'microwave');
}

function objTopY(o) {
  const dy = o.dimensions?.y ?? o.dimensions?.height ?? 0;
  return (o.center?.y ?? 0) + dy / 2;
}
function objBottomY(o) {
  const dy = o.dimensions?.y ?? o.dimensions?.height ?? 0;
  return (o.center?.y ?? 0) - dy / 2;
}
function realCabinetWallRefIds(o) {
  const refs = o.cabinetWallRefs;
  if (!Array.isArray(refs)) return [];
  // Only `real` refs map to room.walls ids; peninsula/island refs point at
  // cabinetLayout.cabinetWalls and aren't wall-region surfaces.
  return refs.filter((r) => r && r.kind === 'real' && r.id).map((r) => r.id);
}

/**
 * Resolve an object's owning real-wall id across both layout schemas:
 *   - materializeCandidate objects carry `parentWallIds`.
 *   - applyKitchenLayout / fromPhaseB objects DROP parentWallIds and carry
 *     `cabinetWallRefs: [{id, kind}]` instead.
 *   - derived objects (countertops) carry neither, only `parents` → resolve
 *     via the parent cabinet's wall.
 * `objById` lets us follow `parents`; `seen` guards against parent cycles.
 * Returns undefined when unresolved.
 */
function wallId0(o, objById, seen) {
  return wallIdsFor(o, objById, seen)[0];
}

/**
 * EVERY real wall this object belongs to, not just the first.
 *
 * A corner piece genuinely belongs to two walls and says so — the corpus has
 * both spellings, `parentWallIds: [wallA, wallB]` and
 * `cabinetWallRefs: [{real, A}, {real, B}]` — because it IS the corner. Taking
 * only `[0]` grouped it onto one wall, so on an L-shaped run the OTHER wall's
 * counter band stopped short by the corner cabinet's depth and left a bare
 * patch at the inside corner. Reported from the app.
 *
 * Order is preserved, so `wallId0` (the single-wall answer, still right for a
 * range, which never spans a corner) keeps returning exactly what it did.
 */
function wallIdsFor(o, objById, seen) {
  const ids = [];
  const add = (id) => {
    if (id && !ids.includes(id)) ids.push(id);
  };
  for (const id of o.parentWallIds || []) add(id);
  for (const id of realCabinetWallRefIds(o)) add(id);
  if (ids.length) return ids;
  // Derived objects (countertops) carry neither and resolve through their
  // parent cabinet — which, at a corner, names both walls itself.
  if (Array.isArray(o.parents) && objById) {
    const visited = seen || new Set();
    if (visited.has(o.id)) return ids;
    visited.add(o.id);
    for (const p of o.parents) {
      const parent = p && objById.get(p.id);
      if (parent)
        for (const id of wallIdsFor(parent, objById, visited)) add(id);
    }
  }
  return ids;
}

// Sub-segments thinner than this (percent of wall length) are dropped — they'd
// be slivers next to the stove, not worth a slab.
const SEG_EPS_PCT = 0.5;
// The widest joint between two pieces of one counter run. Cabinets and slabs
// in a run touch; this only covers the millimetre gaps they are set with, and
// stays far below anything an appliance could stand in.
const COUNTER_JOINT_BRIDGE_M = 0.05;

// A window only blocks a band when their vertical extents genuinely overlap —
// this buffer keeps a sill grazing the band edge from splitting the slab.
const WINDOW_BAND_Y_BUFFER_M = 0.01;

/**
 * Along-wall extents + vertical extents of the wall's windows and door-less
 * cased openings, keyed by wall id. Slim (unhydrated) copies without
 * center/dimensions are skipped — they carry no geometry to subtract.
 */
function openingExtentsByWall(room, wallById) {
  const map = new Map();
  const openings = [...(room?.windows ?? []), ...(room?.openings ?? [])];
  for (const o of openings) {
    if (!o?.center || !o?.dimensions) continue;
    const wid = o.parentWallIds?.[0];
    const wall = wid && wallById.get(wid);
    if (!wall) continue;
    const ext = projectExtentOntoWall([o], wall);
    if (!ext) continue;
    const dy = o.dimensions.y ?? 0;
    if (!map.has(wid)) map.set(wid, []);
    map.get(wid).push({
      min: ext.minP,
      max: ext.maxP,
      bottomY: o.center.y - dy / 2,
      topY: o.center.y + dy / 2,
    });
  }
  return map;
}

/**
 * The room's windows / cased openings that carry NO geometry.
 *
 * `openingExtentsByWall` skips a copy without `center`/`dimensions` in
 * silence, so a band built over one runs straight across the glass and looks
 * deliberate. Every caller that builds bands has to refuse instead — a stale
 * band is a much smaller error than a band over a window.
 *
 * Shared rather than inlined per caller, and that is the whole point: this
 * check lived only inside `rebuildGeneratedBacksplash`, so the
 * `apply_backsplash_texture` tool — which calls `buildBacksplashRegions`
 * DIRECTLY, and is the path a design package's backsplash slot takes — had no
 * guard at all and tiled over the glass whenever hydration came back short.
 *
 * Doors are excluded deliberately: the carve is windows and door-LESS cased
 * openings, so an unhydrated door costs nothing.
 */
function unhydratedOpenings(room) {
  return [...(room?.windows ?? []), ...(room?.openings ?? [])].filter(
    (o) => !(o?.center && o?.dimensions)
  );
}

/** The wall's opening intervals whose vertical extent overlaps [bottomY, topY]. */
function openingBlockersForBand(extents, bottomY, topY) {
  return (extents ?? []).filter(
    (w) =>
      w.bottomY < topY - WINDOW_BAND_Y_BUFFER_M &&
      w.topY > bottomY + WINDOW_BAND_Y_BUFFER_M
  );
}

/**
 * Each wall's LINE — the underside its counter band runs up to — from the
 * uppers that hang over that wall's COUNTER, plus the runs its counter belongs
 * to (for `counterBandTop`'s fallback).
 *
 * Only an upper over the counter counts. The line used to be the lowest
 * underside of every upper on the wall, so a cabinet hanging over something
 * else set it: after a design package put a 36" range and a 36" fridge on one
 * wall, the only upper left was the one over the fridge, refit to the new
 * fridge's top, and the counter beside the range — a window over it — ran its
 * tile up to 87". "Over" is an along-wall overlap wider than a counter joint,
 * so a cabinet merely abutting the counter's end does not count.
 *
 * @returns {Map<string, {lineY: ?number, runIds: Set<string>}>}
 */
function counterLines(byWall, wallById) {
  const lines = new Map();
  for (const [wid, group] of byWall) {
    const wall = wallById.get(wid);
    const surfaces = counterSurfacesOf(group.counter);
    const jointPct = jointBridgePct(wall);
    const counterExts = surfaces
      .map((o) => projectExtentOntoWall([o], wall))
      .filter(Boolean);
    // a valance bay is no cabinet's underside: it sets no line
    const overCounter = group.uppers.filter((u) => {
      if (isValanceBay(u)) return false;
      const e = projectExtentOntoWall([u], wall);
      return (
        e &&
        counterExts.some(
          (c) => Math.min(c.maxP, e.maxP) - Math.max(c.minP, e.minP) > jointPct
        )
      );
    });
    const lineY = overCounter.length
      ? Math.min(...overCounter.map(objBottomY))
      : null;
    lines.set(wid, {
      lineY,
      // How far the line stands above this wall's counter, which is what
      // another wall of the run borrows: the same band, wherever its own
      // counter stands.
      bandHeight:
        lineY != null && surfaces.length
          ? lineY - counterLineY(surfaces)
          : null,
      runIds: new Set(surfaces.map((o) => o.runId).filter(Boolean)),
    });
  }
  return lines;
}

/**
 * Top of the counter band on a wall: its own line (`counterLines`); failing
 * that, the band height its run has on its other walls, so the band of an L
 * whose one leg has nothing over its counter meets the other leg's at the
 * corner; failing that, the default band height above the counter. The stove
 * band reuses this as its no-hood cap — one concept, one source.
 */
function counterBandTop(wid, lines, counterTopY, defaultBand) {
  const own = lines.get(wid);
  if (own?.lineY != null) return own.lineY;
  const runIds = own?.runIds ?? new Set();
  const runHeights = [...lines.values()]
    .filter(
      (l) => l.bandHeight > 0 && [...l.runIds].some((id) => runIds.has(id))
    )
    .map((l) => l.bandHeight);
  return (
    counterTopY + (runHeights.length ? Math.min(...runHeights) : defaultBand)
  );
}

// Two undersides closer than this are one line, and the band keeps it flat.
const SAME_UNDERSIDE_M = 0.001;

/**
 * Split one stretch of counter band where the cabinets ABOVE it change
 * height, so each piece runs up to the underside of what actually hangs over
 * it.
 *
 * `counterBandTop` is one line per wall — the LOWEST underside of the uppers
 * over its counter — and a band capped there cannot follow a cabinet that is shorter
 * than its neighbours. That is exactly what the bay panel's Height chips make:
 * a bridge cabinet keeps its top on the run's line and raises its bottom
 * (`upperBayOverrides`), so a 30" upper set to 12" opened 18" of bare wall
 * between the band and the cabinet, and no re-sync could close it because the
 * line it re-derived was the other uppers'.
 *
 * Where nothing hangs over a stretch — the wall over a sink window, a bay set
 * to "No cabinet" — the band keeps the wall's line, as it always has.
 *
 * Cuts fall at the uppers' edges; two edges closer than `jointPct` (the joint
 * between touching cabinets) are one cut, so a millimetre gap between two
 * uppers never leaves a sliver at the wall's line between them.
 *
 * @param {{min:number,max:number}} span along-wall percent
 * @param {Array<{min:number,max:number,bottomY:number}>} uppers the wall's
 *   uppers, projected onto it
 * @param {number} lineY the wall's line, `counterBandTop`
 * @returns {Array<{min:number,max:number,topY:number}>}
 */
function bandStepsUnderUppers(span, uppers, lineY, jointPct) {
  const steps = [];
  for (const { min, max, over } of partsUnderUppers(span, uppers, jointPct)) {
    const topY = over.length ? Math.min(...over.map((u) => u.bottomY)) : lineY;
    const last = steps[steps.length - 1];
    if (last && Math.abs(last.topY - topY) < SAME_UNDERSIDE_M) last.max = max;
    else steps.push({ min, max, topY });
  }
  return steps;
}

/**
 * The stretches of a span with an upper over them, each with the HIGHEST top
 * among those uppers — where a Full height band carries on above the
 * cabinets to the top of the wall. Cut at the same places as
 * `bandStepsUnderUppers`, so a piece above an upper and the piece under it
 * share their edges.
 *
 * @returns {Array<{min:number,max:number,bottomY:number}>}
 */
function bandStepsAboveUppers(span, uppers, jointPct) {
  const steps = [];
  for (const { min, max, over } of partsUnderUppers(span, uppers, jointPct)) {
    if (!over.length) continue;
    const bottomY = Math.max(...over.map((u) => u.topY));
    const last = steps[steps.length - 1];
    if (
      last &&
      last.max === min &&
      Math.abs(last.bottomY - bottomY) < SAME_UNDERSIDE_M
    )
      last.max = max;
    else steps.push({ min, max, bottomY });
  }
  return steps;
}

/** `COUNTER_JOINT_BRIDGE_M` as a percent of the wall's length. */
function jointBridgePct(wall) {
  const wallLen = wall?.dimensions?.x ?? 0;
  return wallLen ? (COUNTER_JOINT_BRIDGE_M / wallLen) * 100 : 0;
}

/**
 * A span cut at its uppers' edges — edges within a joint of each other are
 * one cut, made at their middle — each part with the uppers over it.
 */
function partsUnderUppers(span, uppers, jointPct) {
  const edges = uppers
    .flatMap((u) => [u.min, u.max])
    .filter((p) => p > span.min + jointPct && p < span.max - jointPct)
    .sort((a, b) => a - b);
  const cuts = [];
  for (const p of edges) {
    const last = cuts[cuts.length - 1];
    if (last && p - last.max <= jointPct) last.max = p;
    else cuts.push({ min: p, max: p });
  }
  const bounds = [span.min, ...cuts.map((c) => (c.min + c.max) / 2), span.max];
  const parts = [];
  for (let i = 0; i < bounds.length - 1; i++) {
    const min = bounds[i];
    const max = bounds[i + 1];
    const mid = (min + max) / 2;
    parts.push({
      min,
      max,
      over: uppers.filter((u) => u.min < mid && u.max > mid),
    });
  }
  return parts;
}

/**
 * Subtract a list of blocker intervals from a span (all in along-wall percent).
 * Returns the leftover sub-spans wider than SEG_EPS_PCT.
 */
function subtractIntervals(span, blockers) {
  const clipped = (blockers || [])
    .filter((b) => b.max > span.min && b.min < span.max)
    .map((b) => ({
      min: Math.max(b.min, span.min),
      max: Math.min(b.max, span.max),
    }))
    .sort((a, b) => a.min - b.min);
  const out = [];
  let cursor = span.min;
  for (const b of clipped) {
    if (b.min > cursor + SEG_EPS_PCT) out.push({ min: cursor, max: b.min });
    cursor = Math.max(cursor, b.max);
  }
  if (cursor < span.max - SEG_EPS_PCT) out.push({ min: cursor, max: span.max });
  return out;
}

/**
 * Merge a list of along-wall extents into the spans they actually cover,
 * bridging joints narrower than `bridgePct`.
 *
 * The counter's own pieces, not their hull. A run is a line of cabinets and
 * counter slabs that touch, so the merge gives back one span per stretch of
 * counter and a gap wherever the counter genuinely stops — at a fridge, at a
 * doorway, at the far side of a room's worth of open wall. The bridge covers
 * the millimetre joints between adjacent pieces without closing anything an
 * appliance could stand in.
 */
function mergeSpans(extents, bridgePct) {
  const sorted = [...extents].sort((a, b) => a.min - b.min);
  const out = [];
  for (const e of sorted) {
    const last = out[out.length - 1];
    if (last && e.min <= last.max + bridgePct) {
      last.max = Math.max(last.max, e.max);
    } else {
      out.push({ min: e.min, max: e.max });
    }
  }
  return out;
}

// Sill/head strips shorter than this aren't worth a slab piece.
const MIN_STRIP_HEIGHT_M = 0.02;

/**
 * The band rects for one along-wall span, carved around window blockers:
 * full-height segments between the windows, PLUS strip rects filling the
 * band below each window's sill and above its head (the splash between the
 * counter and a sink-window sill is real wall — only the glass is off
 * limits). Strips under MIN_STRIP_HEIGHT_M are dropped.
 *
 * @returns {Array<{min,max,bottomY,topY}>}
 */
function bandRectsAroundOpenings(span, blockers, bandBottomY, bandTopY) {
  const rects = subtractIntervals(span, blockers).map((s) => ({
    min: s.min,
    max: s.max,
    bottomY: bandBottomY,
    topY: bandTopY,
  }));
  for (const b of blockers ?? []) {
    const min = Math.max(b.min, span.min);
    const max = Math.min(b.max, span.max);
    if (max - min <= SEG_EPS_PCT) continue;
    const sillY = b.bottomY - WINDOW_BAND_Y_BUFFER_M;
    if (sillY - bandBottomY >= MIN_STRIP_HEIGHT_M) {
      rects.push({ min, max, bottomY: bandBottomY, topY: sillY });
    }
    const headY = b.topY + WINDOW_BAND_Y_BUFFER_M;
    if (bandTopY - headY >= MIN_STRIP_HEIGHT_M) {
      rects.push({ min, max, bottomY: headY, topY: bandTopY });
    }
  }
  return rects;
}

/**
 * The objects on a wall that actually form its COUNTER, out of everything
 * grouped as counter-extent.
 *
 * `lower-cabinet` is not a height. A pantry and a fridge surround are typed
 * `lower-cabinet` and stand 84-88" tall, and 22 of the 411 lower cabinets in
 * the corpus are over 50" — so taking `Math.max` over the group's tops put the
 * counter line at the top of a pantry. On the reported L-run that landed the
 * band's BOTTOM at +33.3" while its top (the wall's upper cabinets' underside)
 * was at -0.5": an inverted band, emitted as nothing, so one leg of the run
 * tiled and the other stayed blank.
 *
 * A countertop settles it outright where the wall has one — it IS the counter.
 * Failing that, the base cabinets do, and a unit standing more than a
 * backsplash gap above the median is not one of them — `DEFAULT_COUNTER_BACKSPLASH_M`,
 * this module's own "how far above the counter a backsplash reaches", because
 * that is exactly the zone in question. The median rather than the minimum
 * because a run's cabinets vary by an inch and a raised bar should still
 * count.
 */
function counterSurfacesOf(counterObjs) {
  const tops = counterObjs.filter(isCountertop);
  if (tops.length) return tops;
  const ys = counterObjs.map(objTopY).sort((a, b) => a - b);
  if (!ys.length) return counterObjs;
  const median = ys[Math.floor(ys.length / 2)];
  const atCounter = counterObjs.filter(
    (o) => objTopY(o) <= median + DEFAULT_COUNTER_BACKSPLASH_M
  );
  return atCounter.length ? atCounter : counterObjs;
}

/** Where the counter surface sits on this wall. */
function counterLineY(counterObjs) {
  return Math.max(...counterSurfacesOf(counterObjs).map(objTopY));
}

/**
 * Along-wall extents of everything solid that CLOSES the counter band's own
 * vertical zone — a fridge, its surround, a pantry, a tall oven column.
 *
 * The rule is still physical rather than a type list, but the test is that the
 * object spans the band END TO END: it stands at or below the counter line AND
 * reaches at or above the band top, so there is no wall left behind it to
 * tile. A fridge and a pantry do that however they are typed
 * (`lower-cabinet`, mostly); an upper cabinet cannot, since its underside IS
 * the band top.
 *
 * Merely OCCUPYING the zone was too loose, and it cost a kitchen the wall
 * behind its sink: a farmhouse sink's rim sits ~6 mm proud of the counter, so
 * the sink read as an obstruction and took its own 30" out of the band, with
 * the faucet carving again inside that. Everything that stands ON a counter —
 * a sink rim, a faucet, a soap dispenser, a kettle — has real wall behind it,
 * and a backsplash is precisely what goes there.
 *
 * With the band's extent now the UNION of the counter's own pieces rather than
 * their hull, this is a backstop rather than the main defence: across the 30
 * corpus kitchens it changes the answer in two, both a wall-oven column
 * standing between the counter line and the uppers' underside.
 */
function bandBlockerExtents({
  objects,
  objById,
  wall,
  wid,
  surfaceIds,
  counterTopY,
  bandTopY,
}) {
  const EPS = 0.005; // 5 mm of slop on both faces — cabinetry is not milled
  const out = [];
  for (const o of objects) {
    if (surfaceIds.has(o.id)) continue; // the counter itself
    if (isUpperCab(o)) continue; // defines the top, does not block
    if (objBottomY(o) > counterTopY + EPS) continue; // stands ON the counter
    if (objTopY(o) < bandTopY - EPS) continue; // stops inside the band
    if (!wallIdsFor(o, objById).includes(wid)) continue;
    const e = projectExtentOntoWall([o], wall);
    if (e) out.push({ min: e.minP, max: e.maxP });
  }
  return out;
}

/**
 * Counter-extent objects and uppers grouped by the wall they stand against —
 * the builder's one reading of "what is on this wall".
 *
 * @returns {Map<string, {counter: Array, uppers: Array}>}
 */
function groupBandSources(objects, walls, objById, wallById) {
  // Group counter-extent objects + uppers by their parent wall.
  const byWall = new Map();
  const ensure = (wid) => {
    if (!byWall.has(wid)) byWall.set(wid, { counter: [], uppers: [] });
    return byWall.get(wid);
  };
  // Onto EVERY wall the object names, not just the first. A corner piece
  // belongs to both walls of the corner, and grouping it onto one left the
  // other wall's band stopping short of the inside corner.
  //
  // A counter surface additionally joins every wall it physically ABUTS.
  // Naming is not reliable here and cannot be made so: an L-corner cabinet
  // names both of its walls, the single-sided corner unit names one, and an
  // ordinary base cabinet whose run just ends at a wall names one CORRECTLY —
  // it is on that wall, it merely butts into the other. All three leave the
  // same return of bare wall above the counter at an inside corner, so the
  // footprint decides. Uppers stay on what they name: an upper's underside
  // sets the band's TOP, and a wall should not inherit a ceiling-height cap
  // from a cabinet hanging on its neighbour. A tall cabinet is no counter at
  // all (`standsThroughBand`), so it joins no wall here, named or abutted.
  for (const o of objects) {
    const isCounterSurface =
      isCountertop(o) || (isLowerCab(o) && !standsThroughBand(o));
    const isUpper = isUpperCab(o);
    if (!isCounterSurface && !isUpper) continue;
    const named = wallIdsFor(o, objById).filter((wid) => wallById.has(wid));
    if (isUpper) {
      for (const wid of named) ensure(wid).uppers.push(o);
      continue;
    }
    const abutted = walls
      .filter((w) => !named.includes(w.id) && objectAbutsWall(o, w))
      .map((w) => w.id);
    for (const wid of [...named, ...abutted]) ensure(wid).counter.push(o);
  }
  return byWall;
}

/**
 * Where a wall's counter is: its surfaces, its stretches along the wall
 * (along-wall percent), the joint bridge and the counter line — or null when
 * nothing of it projects onto the wall.
 */
function counterOnWall(group, wall) {
  // The EXTENT comes from the counter surfaces too, so a band stops at a
  // pantry rather than running across its doors.
  const surfaces = counterSurfacesOf(group.counter);
  const pieces = surfaces
    .map((o) => projectExtentOntoWall([o], wall))
    .filter(Boolean)
    .map((e) => ({ min: e.minP, max: e.maxP }));
  if (!pieces.length) return null;
  // Where the counter IS, not the hull of where it reaches. The hull put a
  // band across every gap in a run — behind a fridge, across a doorway, and
  // in one corpus room across two metres of wall holding nothing but a sofa
  // — and left the obstruction rules to cut those holes back out again,
  // which is a list of things to exclude where the counter already knew.
  const bridgePct = jointBridgePct(wall);
  return {
    surfaces,
    spans: mergeSpans(pieces, bridgePct),
    bridgePct,
    counterTopY: Math.max(...surfaces.map(objTopY)),
  };
}

/**
 * What a wall offers the Backsplash card: whether a counter stands against it
 * (as the builder groups counters — named or abutting), and the sill of a
 * window over that counter, or null. The room's openings must be HYDRATED
 * (`joinRoomOpenings`). Pure.
 */
function wallCounterFacts(room, wallId) {
  const objects = room?.objects ?? [];
  const walls = room?.walls ?? [];
  const wallById = new Map(walls.map((w) => [w.id, w]));
  const objById = new Map(objects.map((o) => [o.id, o]));
  const wall = wallById.get(wallId);
  const group =
    wall && groupBandSources(objects, walls, objById, wallById).get(wallId);
  const counter = group?.counter.length ? counterOnWall(group, wall) : null;
  if (!counter) return { hasCounter: false, sillY: null };
  return {
    hasCounter: true,
    sillY: sillOverCounter(
      openingExtentsByWall(room, wallById).get(wallId),
      counter.spans,
      counter.counterTopY
    ),
  };
}

/**
 * The lines a wall's backsplash settings run to, in world Y — what the
 * Elevations view snaps a dragged band's top edge to (call item #28). Each is
 * read the way the band builder reads it, so a snap is where the card's
 * button puts the band: feed `counter` to `counterLineForSpec` and `range` to
 * `rangeTopForSpec` (`backsplashSpec.js`) with the setting a button writes.
 *
 * - `counter.cabinetsTopY` is To the cabinets as a wall that SETS its counter
 *   reads it — the underside of the uppers over the counter, else the run's
 *   band height, else the default 18" (`counterBandTop`); `sillY` is the
 *   window over the counter whatever the setting (null with none).
 * - `range` reads the first range on the wall: its counter top, the hoods
 *   and the units no tile goes behind over it (`scanOverStove`), and
 *   `counterLineY` — the counter band's line under the wall's CURRENT counter
 *   setting, which Like the counter runs to; with none, the line a rebuild
 *   draws, reading the kitchen's low profile as the rebuild does.
 *
 * The room's openings must be JOINED (`joinRoomOpenings`). A wall that is
 * off, unknown or has nothing to snap to gives null for that half. Pure.
 *
 * @returns {{counter: ?{counterTopY, cabinetsTopY, wallTopY, sillY},
 *   range: ?{counterTopY, counterLineY, wallTopY, capBottoms, hoodBottoms}}}
 */
function wallBandLines(room, wallId) {
  const none = { counter: null, range: null };
  const objects = room?.objects ?? [];
  const walls = room?.walls ?? [];
  const wallById = new Map(walls.map((w) => [w.id, w]));
  const objById = new Map(objects.map((o) => [o.id, o]));
  const wall = wallById.get(wallId);
  if (!wall) return none;
  const spec = readBacksplashSpec(wall);
  if (spec.off) return none;

  const byWall = groupBandSources(objects, walls, objById, wallById);
  const lines = counterLines(byWall, wallById);
  const group = byWall.get(wallId);
  const top = wallTopY(wall);

  const onWall = group?.counter.length ? counterOnWall(group, wall) : null;
  const counter = onWall && {
    counterTopY: onWall.counterTopY,
    cabinetsTopY: counterBandTop(
      wallId,
      lines,
      onWall.counterTopY,
      DEFAULT_COUNTER_BACKSPLASH_M
    ),
    wallTopY: top,
    sillY: sillOverCounter(
      openingExtentsByWall(room, wallById).get(wallId),
      onWall.spans,
      onWall.counterTopY
    ),
  };

  const stove = objects.find(
    (o) => isStove(o) && wallId0(o, objById) === wallId
  );
  const ext = stove && projectExtentOntoWall([stove], wall);
  if (!ext) return { counter, range: null };
  const counterTopY = group?.counter?.length
    ? counterLineY(group.counter)
    : objTopY(stove);
  const { capBottoms, hoodsOver } = scanOverStove(stove, {
    objects,
    objById,
    wid: wallId,
    wall,
    ext,
    counterTopY,
    config: null,
  });
  const lowProfile = allBandsAreLowProfile(
    unsetCounterBands(generatedBandsByKind(walls).byKind)
  );
  const counterLineAtStove =
    spec.counter && counter
      ? counterLineForSpec(spec.counter, {
          ...counter,
          sillY: spec.counter.stop === 'sill' ? counter.sillY : null,
        }).lineY
      : counterBandTop(
          wallId,
          lines,
          counterTopY,
          lowProfile ? LOW_PROFILE_M : DEFAULT_COUNTER_BACKSPLASH_M
        );
  return {
    counter,
    range: {
      counterTopY,
      counterLineY: counterLineAtStove,
      wallTopY: top,
      capBottoms,
      hoodBottoms: hoodsOver.map((h) => h.bottomY),
    },
  };
}

// A hood counts as reached by a band whose top stops within this of its
// underside — what widens a band set to the hood to the hood's width.
const HOOD_REACH_M = 0.005;

/**
 * Each wall's counter, planned once: where its counter is, today's line (To
 * the cabinets) and the line its setting runs the band to. A wall turned off
 * gets no plan, so no counter band.
 *
 * To the cabinets on a wall that SETS it takes the default 18" where nothing
 * hangs, not a tool's low profile: the wall's setting outranks the tool.
 *
 * @returns {Map<string, {wall, surfaces, spans, bridgePct, uppers,
 *   counterTopY, cabinetsTopY, line: {lineY, capAtLine}}>}
 */
function planCounters(
  byWall,
  wallById,
  { lines, defaultBand, specs, openingsByWall }
) {
  const plans = new Map();
  for (const [wid, group] of byWall) {
    if (!group.counter.length) continue;
    const spec = specs.get(wid);
    if (spec?.off) continue;
    const wall = wallById.get(wid);
    const counter = counterOnWall(group, wall);
    if (!counter) continue;
    const { surfaces, spans, bridgePct, counterTopY } = counter;
    const cabinetsTopY = counterBandTop(
      wid,
      lines,
      counterTopY,
      spec?.counter ? DEFAULT_COUNTER_BACKSPLASH_M : defaultBand
    );
    const line = counterLineForSpec(spec?.counter, {
      cabinetsTopY,
      counterTopY,
      wallTopY: wallTopY(wall),
      sillY:
        spec?.counter?.stop === 'sill'
          ? sillOverCounter(openingsByWall.get(wid), spans, counterTopY)
          : null,
    });
    const uppers = group.uppers
      .map((o) => {
        const e = projectExtentOntoWall([o], wall);
        return (
          e && {
            min: e.minP,
            max: e.maxP,
            bottomY: bandTopUnder(o),
            topY: objTopY(o),
          }
        );
      })
      .filter(Boolean);
    plans.set(wid, {
      wall,
      surfaces,
      spans,
      bridgePct,
      uppers,
      counterTopY,
      cabinetsTopY,
      line,
    });
  }
  return plans;
}

/**
 * What hangs directly above a stove on its own wall — the hoods, and the
 * units no tile goes behind (an over-range cabinet or microwave) — plus
 * today's caps for a wall with no range setting (an upper always; a hood
 * when the style runs the band to it). Shared by the band builder and
 * `wallBandLines`, so a snap and a band read the same objects.
 */
function scanOverStove(
  stove,
  { objects, objById, wid, wall, ext, counterTopY, config }
) {
  const overStoveBottoms = [];
  const cappingHoodExtents = [];
  const capBottoms = [];
  const caps = [];
  const hoodsOver = [];
  for (const cand of objects) {
    if (cand === stove) continue;
    const candIsHood = isHood(cand);
    if (!candIsHood && !isUpperCab(cand) && !isMicrowave(cand)) continue;
    if (!wallIdsFor(cand, objById).includes(wid)) continue;
    const candExt = projectExtentOntoWall([cand], wall);
    if (!candExt) continue;
    const overlap =
      Math.min(candExt.maxP, ext.maxP) - Math.max(candExt.minP, ext.minP);
    if (overlap <= SEG_EPS_PCT) continue;
    const candBottomY = objBottomY(cand);
    if (candBottomY <= counterTopY) continue;
    const bandTopY = candIsHood ? hoodBandTopY(cand) : candBottomY;
    if (candIsHood) hoodsOver.push({ ext: candExt, bottomY: bandTopY });
    else {
      capBottoms.push(candBottomY);
      caps.push({ ext: candExt, topY: objTopY(cand) });
    }
    // a wood hood is open at the back: the band goes up to its insert
    // whatever the style says
    const capsToday = candIsHood
      ? isWoodHood(cand) || config?.stoveBacksplash?.toHood === true
      : isUpperCab(cand);
    if (!capsToday) continue;
    overStoveBottoms.push(bandTopY);
    if (candIsHood) cappingHoodExtents.push(candExt);
  }
  return { overStoveBottoms, cappingHoodExtents, capBottoms, caps, hoodsOver };
}

function buildBacksplashRegions(room, config, textureIds = {}, opts = {}) {
  const objects = room?.objects ?? [];
  const walls = room?.walls ?? [];
  const wallById = new Map(walls.map((w) => [w.id, w]));
  const objById = new Map(objects.map((o) => [o.id, o]));
  const regions = [];
  const provenance = {
    tool: 'style_kitchen',
    styleId: config?.resolvedStyle ?? null,
    generatedAt: opts.generatedAt ?? null,
  };

  const byWall = groupBandSources(objects, walls, objById, wallById);

  const counterTex = textureIds.counterBacksplash ?? null;
  const stoveTex = config?.combineBacksplash
    ? counterTex
    : (textureIds.stoveBacksplash ?? counterTex);

  const lowProfile = config?.counterBacksplash?.lowProfile === true;
  const defaultBand = lowProfile ? LOW_PROFILE_M : DEFAULT_COUNTER_BACKSPLASH_M;
  const lines = counterLines(byWall, wallById);
  // Windows + cased openings per wall — bands are carved around them below.
  const openingsByWall = openingExtentsByWall(room, wallById);
  // Each wall's own setting (`backsplashSpec.js`) — it outranks the tool's
  // `config`, which outranks a rebuild's inference. None = today's band.
  const specs = new Map(walls.map((w) => [w.id, readBacksplashSpec(w)]));
  const counterPlans = planCounters(byWall, wallById, {
    lines,
    defaultBand,
    specs,
    openingsByWall,
  });

  // Plan every stove band UP FRONT — extent and top — because two consumers
  // need the extent and they must agree exactly: the counter band is carved
  // around it, and the stove band itself is emitted from it. Deriving the
  // extent twice is how the band and the hole cut for it drift apart, and a
  // coplanar overlap on a wall plane z-fights.
  const stovePlans = [];
  for (const o of objects) {
    if (!isStove(o)) continue;
    const wid = wallId0(o, objById);
    const wall = wallById.get(wid);
    if (!wall) continue;
    const spec = specs.get(wid);
    if (spec?.off) continue; // a wall turned off gets no band at all
    const ext = projectExtentOntoWall([o], wall);
    if (!ext) continue;
    const wallGroup = byWall.get(wid);
    const counterTopY = wallGroup?.counter?.length
      ? counterLineY(wallGroup.counter)
      : objTopY(o);

    // What hangs directly above this stove on the same wall: an over-range
    // cabinet, or a hood when the style asks for one. A hood elsewhere in the
    // room — a floating island hood over a peninsula — must never cap a wall
    // band, which is what the same-wall + span-overlap test enforces.
    //
    // `overStoveBottoms` / `cappingHoodExtents` are today's rule, for a wall
    // with no range setting. A setting reads the hoods and the units no tile
    // goes behind (an over-range cabinet or microwave) on its own terms
    // (`rangeTopForSpec`).
    const {
      overStoveBottoms,
      cappingHoodExtents,
      capBottoms,
      caps,
      hoodsOver,
    } = scanOverStove(o, {
      objects,
      objById,
      wid,
      wall,
      ext,
      counterTopY,
      config,
    });

    // The counter band's line at this range: the wall's own setting when it
    // has one (Like the counter, and a range with no hood, follow it), else
    // today's line.
    const counterLineAtStove =
      spec?.counter && counterPlans.has(wid)
        ? counterPlans.get(wid).line.lineY
        : counterBandTop(wid, lines, counterTopY, defaultBand);
    let bandTopY;
    let widenTo;
    if (spec?.range) {
      bandTopY = rangeTopForSpec(spec.range, {
        counterTopY,
        counterLineY: counterLineAtStove,
        wallTopY: wallTopY(wall),
        capBottoms,
        hoodBottoms: hoodsOver.map((h) => h.bottomY),
      });
      // a hood widens the band once the band reaches it
      widenTo = hoodsOver
        .filter((h) => h.bottomY <= bandTopY + HOOD_REACH_M)
        .map((h) => h.ext);
    } else {
      bandTopY = overStoveBottoms.length
        ? Math.min(...overStoveBottoms)
        : counterLineAtStove;
      widenTo = cappingHoodExtents;
    }

    // The band takes the HOOD's width, not the range's, whenever a hood caps
    // it. A hood is specified wider than the range it vents (a 30" range
    // normally takes a 36" hood), and a band cut to the range leaves two bare
    // strips of wall under the hood's overhang — visible precisely because
    // the tall band draws the eye there.
    //
    // UNION rather than replace: the band must never come out NARROWER than
    // the appliance it stands behind, and a downdraft or a compact insert can
    // measure narrower than its range. Only a HOOD widens it — an over-range
    // cabinet caps the band's height but must not stretch it out behind the
    // flanking uppers.
    const bandExt = widenTo.reduce(
      (acc, h) => ({
        minP: Math.min(acc.minP, h.minP),
        maxP: Math.max(acc.maxP, h.maxP),
      }),
      { minP: ext.minP, maxP: ext.maxP }
    );

    // To the ceiling carries on above what capped it — an over-range cabinet
    // or microwave — to the top of the wall, as Full height does above the
    // uppers.
    const capsAbove = spec?.range?.stop === 'full' ? caps : [];

    stovePlans.push({
      wid,
      wall,
      wallGroup,
      counterTopY,
      bandExt,
      bandTopY,
      capsAbove,
    });
  }

  // The counter band is carved around the PLANNED stove bands (hood width
  // included), not around the ranges themselves.
  const stoveExtentsByWall = new Map();
  for (const plan of stovePlans) {
    if (!stoveExtentsByWall.has(plan.wid)) stoveExtentsByWall.set(plan.wid, []);
    stoveExtentsByWall
      .get(plan.wid)
      .push({ min: plan.bandExt.minP, max: plan.bandExt.maxP });
  }

  /** Emit one region per band rect (skipping degenerate ones). */
  const pushRects = (rects, wall, wid, textureId, kind) => {
    for (const r of rects) {
      const region = bandRegion({
        minP: r.min,
        maxP: r.max,
        bottomY: r.bottomY,
        topY: r.topY,
        wall,
      });
      if (region) {
        regions.push({
          wallId: wid,
          region,
          surfaceType: 'backsplash',
          type: 'texture',
          textureId,
          styleProvenance: provenance,
          kind,
        });
      }
    }
  };

  /**
   * Emit the pieces ABOVE a cabinet — `{min, max, bottomY}`, from the
   * cabinet's top to the top of the wall — carved round any glass up there.
   * A cabinet reaching the top of the wall leaves nothing.
   */
  const pushAbove = (pieces, wall, wid, textureId, kind) => {
    const topY = wallTopY(wall);
    for (const piece of pieces) {
      if (piece.max - piece.min <= SEG_EPS_PCT || topY <= piece.bottomY)
        continue;
      const blockers = openingBlockersForBand(
        openingsByWall.get(wid),
        piece.bottomY,
        topY
      );
      pushRects(
        bandRectsAroundOpenings(piece, blockers, piece.bottomY, topY),
        wall,
        wid,
        textureId,
        kind
      );
    }
  };

  // Counter backsplash: one band per stretch of counter on the wall, split
  // around any stove(s) on it and carved around windows (sill/head strips
  // kept) within each segment. The wall's plan (`planCounters`) holds where
  // its counter is and the line its setting runs the band to.
  for (const [wid, plan] of counterPlans) {
    const { wall, surfaces, spans, counterTopY, cabinetsTopY, line } = plan;
    const carve = [
      ...(stoveExtentsByWall.get(wid) ?? []),
      // A tall unit standing in a stretch of counter interrupts it the way a
      // stove does — an oven column between the counter line and the uppers.
      // Judged against To the cabinets whatever the setting, so a Full height
      // band never tiles a strip above an oven column.
      ...bandBlockerExtents({
        objects,
        objById,
        wall,
        wid,
        surfaceIds: new Set(surfaces.map((o) => o.id)),
        counterTopY,
        bandTopY: cabinetsTopY,
      }),
    ];
    for (const span of spans) {
      for (const seg of subtractIntervals(span, carve)) {
        // Each step carves against its OWN top: a window head that clears
        // the wall's line can still stand in the taller band under a bridge.
        for (const step of capStepsAtLine(
          bandStepsUnderUppers(seg, plan.uppers, line.lineY, plan.bridgePct),
          line
        )) {
          const blockers = openingBlockersForBand(
            openingsByWall.get(wid),
            counterTopY,
            step.topY
          );
          pushRects(
            bandRectsAroundOpenings(step, blockers, counterTopY, step.topY),
            wall,
            wid,
            counterTex,
            'counter'
          );
        }
        // Full height is the whole wall but the cabinets: on above each
        // upper to the top of the wall, round any glass up there.
        if (specs.get(wid)?.counter?.stop === 'full') {
          pushAbove(
            bandStepsAboveUppers(seg, plan.uppers, plan.bridgePct),
            wall,
            wid,
            counterTex,
            'counter'
          );
        }
      }
    }
  }

  // Stove backsplash: emit the bands planned above. Everything that decides
  // WHERE the band goes was settled up front so the counter carve and the
  // band itself can never disagree; all that is left here is the window
  // carve and the emit.
  for (const plan of stovePlans) {
    const blockers = openingBlockersForBand(
      openingsByWall.get(plan.wid),
      plan.counterTopY,
      plan.bandTopY
    );
    pushRects(
      bandRectsAroundOpenings(
        { min: plan.bandExt.minP, max: plan.bandExt.maxP },
        blockers,
        plan.counterTopY,
        plan.bandTopY
      ),
      plan.wall,
      plan.wid,
      stoveTex,
      'stove'
    );
    // Above the HIGHEST of what is stacked over the range (a cabinet over a
    // microwave), never behind it — the uppers' rule.
    pushAbove(
      bandStepsAboveUppers(
        { min: plan.bandExt.minP, max: plan.bandExt.maxP },
        plan.capsAbove.map((cap) => ({
          min: cap.ext.minP,
          max: cap.ext.maxP,
          topY: cap.topY,
        })),
        jointBridgePct(plan.wall)
      ),
      plan.wall,
      plan.wid,
      stoveTex,
      'stove'
    );
  }

  return regions;
}

/**
 * Tools that write generated kitchen backsplash bands.
 *
 * Read by `isGeneratedKitchenBacksplash` as one of the two signals that a band
 * is machine-derived. NOT the clear filter — `mergeBacksplash` takes every
 * backsplash region on the wall, because a band written by the client-side
 * "Add Backsplash" path carries no provenance at all and a provenance-scoped
 * clear left it behind to z-fight with the new one.
 */
const BACKSPLASH_PROVENANCE_TOOLS = new Set([
  'style_kitchen',
  'apply_backsplash_texture',
  // the default bands a layout apply draws (`defaultKitchenBacksplash.js`)
  'kitchen-layout',
]);

/**
 * The `groupId` prefix every generated kitchen band carries.
 *
 * Deliberately still spelled `style_kitchen_backsplash` now that a second tool
 * writes these: it is PERSISTED on every band in the corpus, and it names the
 * kind of thing (a cabinet-derived band split into segments) rather than which
 * tool happened to produce it. Renaming it would orphan every existing region
 * from the predicate below.
 */
const KITCHEN_BACKSPLASH_GROUP_PREFIX = 'style_kitchen_backsplash';

/**
 * Is this region a GENERATED kitchen backsplash band?
 *
 * The viewer suppresses drag handles for these: the bounds are derived from
 * the cabinets rather than hand-drawn, they are one band split around the
 * stove, and only the focused segment would get handles anyway — you re-tile
 * them instead. Bathroom and hand-drawn backsplashes keep their handles.
 *
 * A shared predicate rather than a `groupId.startsWith(...)` at the call site,
 * because that string test was the only thing standing between a generated
 * band and a set of handles that cannot move it, and a second writer is
 * exactly how such a test silently stops matching. Either signal qualifies:
 * the group prefix (what every persisted band carries) or the provenance tool.
 */
function isGeneratedKitchenBacksplash(region) {
  if (region?.surfaceType !== 'backsplash') return false;
  if (
    typeof region.groupId === 'string' &&
    region.groupId.startsWith(KITCHEN_BACKSPLASH_GROUP_PREFIX)
  ) {
    return true;
  }
  return BACKSPLASH_PROVENANCE_TOOLS.has(region?.styleProvenance?.tool);
}

/**
 * The generated bands already on these walls, optionally narrowed to one kind.
 *
 * Matches on `surfaceType` alone rather than on provenance: a band written by
 * an older build, or one whose provenance was dropped somewhere in its
 * history, is still the band the user is looking at and still the one they
 * mean to re-tile. Provenance decides what may be REPLACED (see
 * `mergeBacksplash`), which is a destructive act and rightly stricter.
 */
function findBacksplashRegions(walls, kind) {
  const found = [];
  for (const wall of walls || []) {
    for (const region of wall?.texture?.regions || []) {
      if (region?.surfaceType !== 'backsplash') continue;
      if (kind && region.kind !== kind) continue;
      found.push({ wall, region });
    }
  }
  return found;
}

/**
 * Re-texture in place. Returns the new walls array, or null when nothing
 * matched — the signal to synthesize instead.
 *
 * `id` carries the TEXTURE id (the renderer resolves it through
 * `getTextureById(region.id)`), matching what `mergeBacksplash` writes. Any
 * `paintColor` on the region is cleared: the renderers check paint first, so
 * one left behind would hide the tile that was just applied — the same rule
 * the cabinet and countertop appliers follow. A tile applied here is a tile
 * CHOSEN, so a band drawn in the default tile loses its not-chosen flag
 * (`unchosenBacksplash.js`) — on the kind re-tiled only.
 */
function retextureBacksplashWalls(walls, textureId, kind) {
  let touched = 0;
  const out = (walls || []).map((wall) => {
    const regions = wall?.texture?.regions;
    if (!Array.isArray(regions)) return wall;
    let wallTouched = false;
    const next = regions.map((region) => {
      if (region?.surfaceType !== 'backsplash') return region;
      if (kind && region.kind !== kind) return region;
      wallTouched = true;
      touched += 1;
      const updated = clearUnchosenBacksplash({ ...region, id: textureId });
      delete updated.paintColor;
      return updated;
    });
    if (!wallTouched) return wall;
    return { ...wall, texture: { ...wall.texture, regions: next } };
  });
  return touched ? { walls: out, touched } : null;
}

/**
 * Replace the GENERATED backsplash bands on `walls` with `regions`, leaving
 * every other region alone.
 *
 * The cloud's `mergeBacksplash` strips every region whose `surfaceType` is
 * `backsplash` before writing — fine when a person just asked for a backsplash,
 * destructive when the rebuild is a SIDE EFFECT of moving a range. A band the
 * user drew by hand is not ours to discard on a resize, so this narrows the
 * strip to what `isGeneratedKitchenBacksplash` claims.
 */
function replaceGeneratedBacksplash(walls, regions) {
  const out = (walls || []).map((w) => {
    const texture = w.texture ? { ...w.texture } : {};
    const existing = Array.isArray(texture.regions) ? texture.regions : [];
    texture.regions = existing.filter((r) => !isGeneratedKitchenBacksplash(r));
    return { ...w, texture };
  });
  const byId = new Map(out.map((w) => [w.id, w]));
  const stamp = regions[0]?.styleProvenance?.generatedAt ?? 'gen';
  const groupId = `${KITCHEN_BACKSPLASH_GROUP_PREFIX}_${stamp}`;
  regions.forEach((r, idx) => {
    const w = byId.get(r.wallId);
    if (!w) return;
    w.texture.regions.push({
      id: r.textureId ?? null,
      regionId: `backsplash_${r.kind}_${r.wallId}_${idx}`,
      groupId,
      ...r.region,
      surfaceType: 'backsplash',
      type: 'texture',
      styleProvenance: r.styleProvenance,
      kind: r.kind,
      // drawn in the default tile, nobody's pick yet (`unchosenBacksplash.js`)
      ...(r.unchosen ? { unchosen: true } : {}),
    });
  });
  return out;
}

/**
 * Is this an object a band can be built from — a counter surface (countertop
 * or base cabinet) or a range? The same predicates `buildBacksplashRegions`
 * groups by, so "no source left" means exactly "the builder has nothing" —
 * which is when a room's generated bands should go (`backsplashResync`), as
 * against an empty build over counters it could not seat on a wall, when
 * removing them would be a guess.
 */
function isBandSource(o) {
  return !!o && (isCountertop(o) || isLowerCab(o) || isStove(o));
}

/**
 * Take every GENERATED band off these walls and leave everything else — a
 * hand-drawn band, a shower surround, the wall's paint. Walls that carried no
 * generated band come back by identity.
 */
function stripGeneratedBacksplash(walls) {
  return (walls || []).map((w) => {
    const regions = w?.texture?.regions;
    if (!Array.isArray(regions) || !regions.some(isGeneratedKitchenBacksplash))
      return w;
    return {
      ...w,
      texture: {
        ...w.texture,
        regions: regions.filter((r) => !isGeneratedKitchenBacksplash(r)),
      },
    };
  });
}

/** Object types a generated band's geometry is DERIVED from. */
const BACKSPLASH_SOURCE_TYPES = new Set([
  'countertop',
  'lower-cabinet',
  'upper-cabinet',
  'tall-cabinet',
  'corner-cabinet',
  'range',
  'stove',
  'cooktop',
  'vent-hood',
  'range-hood',
  // Not just what the band is MEASURED from — anything that interrupts it.
  // A fridge moved along its wall leaves the band crossing where it used to
  // stand, which is what made this list's first version too short.
  'refrigerator',
  'fridge-surround',
  'dishwasher',
  'wall-oven',
]);

/** Does moving these objects change where a band should be? */
function affectsBacksplash(objects) {
  for (const o of objects || []) {
    const t = (o?.type || '').toLowerCase();
    if (BACKSPLASH_SOURCE_TYPES.has(t)) return true;
    if (o?.category && typeof o.category === 'object') {
      for (const k of Object.keys(o.category)) {
        if (o.category[k] && BACKSPLASH_SOURCE_TYPES.has(k)) return true;
      }
    }
  }
  return false;
}

/**
 * Give the room's own openings their geometry, from the structure copies.
 *
 * Neither side can carve a window alone. A room's copy names the ROOM wall and
 * post-#634 carries no geometry; the structure's copy carries the geometry and
 * names a STRUCTURE wall. Measured on the reported kitchen: its window names
 * room wall `914642AF` while its structure copy names `6515C8EA`, and 0 of the
 * structure's 13 openings resolve onto any of that room's 4 walls. Feeding
 * either side in unchanged produced no carve at all, and because
 * `openingExtentsByWall` SKIPS a copy it cannot measure rather than failing,
 * that was silent — the band simply ran over the glass.
 *
 * Deliberately narrower than `openings/openingSync.hydrateRoomOpenings`, which
 * is the general join: that also merges trim and appearance, and lives in a
 * module the web app does not declare as a dependency. A carve needs a
 * footprint and a wall, so that is all this takes — geometry from the
 * structure copy, wall association from the room copy.
 *
 * A no-op when the room's copies already carry geometry (the cloud path hands
 * over an already-hydrated room) or when no structure arrays are supplied.
 */
function joinRoomOpenings(room, structureOpenings) {
  if (!structureOpenings) return room;
  const byId = new Map();
  for (const list of [structureOpenings.windows, structureOpenings.openings])
    for (const o of list || []) if (o?.id) byId.set(o.id, o);
  if (!byId.size) return room;
  const join = (list) =>
    (list || []).map((o) => {
      if (o?.center && o?.dimensions) return o;
      const src = byId.get(o?.structureOpeningId);
      if (!src?.center || !src?.dimensions) return o;
      return {
        ...o,
        center: src.center,
        dimensions: src.dimensions,
        rotation: src.rotation ?? o.rotation,
      };
    });
  return {
    ...room,
    windows: join(room.windows),
    openings: join(room.openings),
  };
}

/**
 * Rebuild a room's generated backsplash bands against its CURRENT geometry.
 *
 * The bands are derived — the counter band spans the run and stops under the
 * uppers, the stove band rises behind the range and takes the hood's width —
 * but nothing recomputed them once they were written. Resize a range and the
 * band behind it keeps the old range's width; move a tall cabinet and the
 * counter band still runs to where it used to stand.
 *
 * The settings are read back off the bands being replaced rather than from a
 * style config, because at the moment a range is resized there is no config in
 * hand and the honest answer to "how should this be tiled" is "the way it
 * already is". Texture ids are carried forward per KIND, so a counter band and
 * a stove band tiled differently stay that way.
 *
 * @param {Object} room     the room, with `walls`, `objects`, and — see below
 *                          — HYDRATED `windows` / `openings`.
 * @returns {Array|null}    the new `walls`, or null when there is nothing to
 *                          rebuild or not enough information to do it safely.
 */
/** The kitchen's generated bands on `walls`, and the same grouped by kind. */
function generatedBandsByKind(walls) {
  const existing = findBacksplashRegions(walls).filter(({ region }) =>
    isGeneratedKitchenBacksplash(region)
  );
  const byKind = new Map();
  for (const entry of existing) {
    const kind = entry.region.kind;
    if (!byKind.has(kind)) byKind.set(kind, []);
    byKind.get(kind).push(entry);
  }
  return { existing, byKind };
}

/**
 * The counter bands a rebuild may read the kitchen's settings from: those on
 * walls with no counter setting of their own (see the rebuild).
 */
const unsetCounterBands = (byKind) =>
  (byKind.get('counter') || []).filter((e) => !wallSetsBand(e.wall, 'counter'));

function rebuildGeneratedBacksplash(room, opts = {}) {
  const walls = room?.walls ?? [];
  const { existing, byKind } = generatedBandsByKind(walls);
  if (!existing.length) return null; // nothing of ours to rebuild

  // Bands carve around windows and cased openings, and
  // `openingExtentsByWall` SKIPS a copy without geometry rather than failing
  // — so a rebuild over slim copies silently tiles straight across the glass.
  // Refuse instead: a stale band is a much smaller error than a band over a
  // window.
  //
  // The caller is responsible for handing over a room whose openings have
  // been JOINED to their structure copies (`hydrateRoomOpenings`), because
  // neither copy can do this alone: the room copy names the ROOM wall and
  // carries no geometry, the structure copy carries geometry and names a
  // STRUCTURE wall. Measured on the reported kitchen — its window names room
  // wall `914642AF` while its structure copy names `6515C8EA`, and 0 of the
  // structure's 13 openings resolve onto any of this room's 4 walls.
  //
  // EVERY opening, not merely one of them. This was `!all.some(...)` — it
  // refused only when NOTHING had hydrated, so a room where some openings
  // resolved and others did not sailed through and tiled over exactly the
  // ones that had not. `openingExtentsByWall` skips a geometry-less copy
  // silently, so the band came out looking deliberate. Partial hydration is
  // ordinary, not an edge case: 1 of the 9 corpus kitchens with openings has
  // it (EC6A20EB, 2 of 3), which is why the symptom was "SOMETIMES the
  // backsplash covers the window". Rooms where nothing hydrates already
  // refused, so requiring all of them narrows nothing else.
  const joined = joinRoomOpenings(room, opts.structureOpenings);
  if (unhydratedOpenings(joined).length) return null;

  // Settings recovered from what is on the wall today. Per kind, the texture
  // MOST of that kind's bands wear — not the first one found. Bands of a kind
  // are re-tiled together, so a band carrying a different id is a straggler
  // from an earlier apply that some later pass missed; taking whichever
  // happened to be first put a stale tile, and once the kitchen's stove tile,
  // onto every counter band.
  const dominant = (kind) => {
    const entries = byKind.get(kind);
    if (!entries?.length) return undefined;
    const tally = new Map();
    for (const e of entries) {
      if (!e.region?.id) continue;
      tally.set(e.region.id, (tally.get(e.region.id) || 0) + 1);
    }
    let best = null;
    for (const [id, n] of tally) if (!best || n > best[1]) best = [id, n];
    return best
      ? { ...entries[0], region: { ...entries[0].region, id: best[0] } }
      : entries[0];
  };
  const counter = dominant('counter');
  const stove = dominant('stove');
  // `buildBacksplashRegions` keys these `counterBacksplash` / `stoveBacksplash`
  // — the tool's PARAMETER names, not the region's `kind`. Using `kind` here
  // read back as "no texture" and rebuilt every band untextured, which is a
  // blank wall rather than a stale band: strictly worse than doing nothing.
  // A kind with no band left takes the OTHER kind's tile. The builder
  // already gives a range band with none of its own the counter's tile; a
  // counter band with none of its own — every counter wall turned off, then
  // one turned back on — came back with no tile at all, a blank band.
  const counterId = counter?.region?.id ?? stove?.region?.id;
  const textureIds = {
    ...(counterId ? { counterBacksplash: counterId } : {}),
    ...(stove?.region?.id ? { stoveBacksplash: stove.region.id } : {}),
  };
  // The guesses below read only bands on walls with no setting for that band:
  // a wall's own setting (`backsplashSpec.js`) already says how IT is drawn,
  // and read as evidence it told the rest of the kitchen something false — a
  // wall set to a 4" splash made a range band at the counter's 18" look "run
  // to the hood", and a wall set to Full height made a low-profile kitchen
  // look tall.
  const unsetCounters = unsetCounterBands(byKind);
  // A range band on a wall that sets its COUNTER is no evidence either: with
  // no hood to stop at, it follows the counter's set line.
  const unsetStove = (byKind.get('stove') || []).find(
    (e) => !wallSetsBand(e.wall, 'range') && !wallSetsBand(e.wall, 'counter')
  );
  const config = {
    resolvedStyle: existing[0].region?.styleProvenance?.styleId ?? null,
    // A stove band that is a separate region from the counter band means the
    // two were NOT combined; one band doing both duties means they were.
    combineBacksplash: !stove && !!counter,
    counterBacksplash: {
      // Judged across EVERY counter band, not the one `dominant` happened to
      // return — that is `entries[0]`, i.e. whichever wall came first, and a
      // kitchen's bands are routinely different heights (a wall with uppers
      // stops under them, one without runs the default). One short segment
      // would have rebuilt the whole kitchen at 3".
      lowProfile: allBandsAreLowProfile(unsetCounters),
    },
    // A stove band taller than the counter band was run to the hood.
    stoveBacksplash: { toHood: inferToHood(byKind, unsetStove, unsetCounters) },
  };

  // No texture recovered means the rebuild would write bands with `id: null`
  // — a blank wall where tile used to be. Leave the stale band instead.
  if (!Object.keys(textureIds).length) return null;

  // The not-chosen flag rides with the tile, per kind: a band re-fitted after
  // a cabinet moved is still the default tile nobody picked. A kind with no
  // band of its own yet (a range added since, a counter wall turned back on)
  // is tiled like the other kind, so it is as chosen as that kind is.
  const unchosen = (kind) => {
    const entries = byKind.get(kind);
    if (!entries?.length) {
      const other = kind === 'counter' ? 'stove' : 'counter';
      return !!byKind.get(other)?.length && unchosen(other);
    }
    const id = dominant(kind)?.region?.id;
    const ofTile = entries.filter((e) => e.region?.id === id);
    return (
      ofTile.length > 0 && ofTile.every((e) => isUnchosenBacksplash(e.region))
    );
  };

  const regions = buildBacksplashRegions(joined, config, textureIds, {
    generatedAt: opts.generatedAt ?? new Date().toISOString(),
  }).map((r) => (unchosen(r.kind) ? { ...r, unchosen: true } : r));
  if (!regions.length) return null;
  return replaceGeneratedBacksplash(walls, regions);
}

/**
 * A band's height in METRES.
 *
 * `bandRegion` stores a region as PERCENTAGES of the wall it sits on, so a
 * height read straight off the region is a number like `14.6` and comparing it
 * to a metre constant silently answers "not low profile" for everything.
 */
function bandHeightMeters(entry) {
  // A share of the DRAWN face, as `bandRegion` writes it (`wallFace`).
  const wallHeight = wallFaceHeight(entry?.wall) ?? 0;
  const pct = entry?.region?.height ?? 0;
  return (pct / 100) * wallHeight;
}

/** A 3" band is low-profile; the default is 18". Split the difference. */
function bandIsLowProfile(entry) {
  const h = bandHeightMeters(entry);
  return h > 0 && h <= (LOW_PROFILE_M + DEFAULT_COUNTER_BACKSPLASH_M) / 2;
}

/**
 * Was the kitchen DESIGNED low-profile?
 *
 * Every measurable band has to be short. A low-profile kitchen is one where
 * someone asked for a 3" splash throughout (`lowProfileBacksplash` on
 * `style_kitchen` / `apply_backsplash_texture` — nothing else ever sets it),
 * so a single tall band is proof they did not: whatever is short is short for
 * another reason, and rebuilding the room around it loses the tall run.
 *
 * Bands with no measurable height are ignored rather than counted either way —
 * `bandHeightMeters` returns 0 when the wall carries no height, and treating
 * that as "short" is how an unmeasurable band would decide the whole kitchen.
 */
function allBandsAreLowProfile(entries) {
  const measurable = (entries || []).filter((e) => bandHeightMeters(e) > 0);
  return measurable.length > 0 && measurable.every(bandIsLowProfile);
}

/**
 * Was the range band run to the hood? Read from a range band on a wall with
 * no setting at all, against the counter bands on walls with no counter
 * setting (`bandRisesAboveCounter`). Failing that, from a range band on a
 * wall that sets only its COUNTER, against that wall's own counter bands: a
 * range band left level with them follows the counter (no hood); one taller
 * or shorter was stopped by something else — the hood. With neither, the
 * default every writer asks for: to the hood.
 */
function inferToHood(byKind, unsetStove, unsetCounters) {
  if (unsetStove) return bandRisesAboveCounter(unsetStove, unsetCounters);
  const onCounterSetWall = (byKind.get('stove') || []).find(
    (e) => !wallSetsBand(e.wall, 'range')
  );
  if (!onCounterSetWall) return true;
  const ownCounters = (byKind.get('counter') || []).filter(
    (e) => e.wall === onCounterSetWall.wall
  );
  const h = bandHeightMeters(onCounterSetWall);
  return !ownCounters.some(
    (c) => Math.abs(bandHeightMeters(c) - h) < LEVEL_WITH_COUNTER_M
  );
}

// A range band within this of its wall's counter band runs level with it.
const LEVEL_WITH_COUNTER_M = 0.01;

/**
 * Does the stove band rise past where the counter band stops?
 *
 * Against the SHORTEST counter band, not one picked at random. A counter band
 * runs up to whatever hangs over it, so the piece under a bridge cabinet
 * stands taller than the rest — 36" under a 12" bridge, against a 30" band to
 * the hood — and `dominant('counter')` is whichever band came first. Read
 * against that one, a bridge at the start of a run turned "to the hood" off
 * and the next rebuild dropped the stove band to the run's line. The shortest
 * leans toward `true`, which is what every writer asks for.
 */
function bandRisesAboveCounter(stove, counters) {
  const heights = (counters || []).map(bandHeightMeters).filter((h) => h > 0);
  if (!heights.length) return true;
  return bandHeightMeters(stove) > Math.min(...heights) + 0.01;
}

module.exports = {
  buildBacksplashRegions,
  unhydratedOpenings,
  joinRoomOpenings,
  rebuildGeneratedBacksplash,
  replaceGeneratedBacksplash,
  affectsBacksplash,
  isBandSource,
  stripGeneratedBacksplash,
  wallCounterFacts,
  wallBandLines,
  findBacksplashRegions,
  retextureBacksplashWalls,
  DEFAULT_COUNTER_BACKSPLASH_M,
  LOW_PROFILE_M,
  // how far under a sill (and over a head) a band stops — what tells the
  // Elevations drag a band piece cut at the glass
  WINDOW_BAND_Y_BUFFER_M,
  BACKSPLASH_PROVENANCE_TOOLS,
  KITCHEN_BACKSPLASH_GROUP_PREFIX,
  isGeneratedKitchenBacksplash,
  // What the builder reads a band from, for the wall panel's Backsplash card
  // (the wall's sketch and what it offers) — one reading, not a second copy.
  wallIdsFor,
  isCountertop,
  isLowerCab,
  isUpperCab,
  isStove,
  isHood,
  isMicrowave,
  standsThroughBand,
};
